import * as FirebaseAuth from "firebase/auth"
import {
  Variant,
  DealRentals,
  RentalsMonthlyBreakdown,
  BookingsByMonth,
} from "@flypengine/myFlyp-lib"
import * as E from "fp-ts/lib/Either"
import * as O from "fp-ts/lib/Option"
import * as A from "fp-ts/lib/Array"
import * as N from "fp-ts/lib/number"
import { PageStatus } from "../../lib/types"
import { HttpError } from "elm-ts/lib/Http"
import { pipe } from "fp-ts/lib/function"
import { contramap } from "fp-ts/lib/Ord"

export type Config<msg> = {
  propcode: string
  user: FirebaseAuth.User
  wrapperMsg: (m: Msg) => msg
  dealRentalsMsg: () => msg
}

export type Model = E.Either<PageStatus, RentalsModel>

export type RentalsModel = {
  finantialPositionModel: FinantialPosition
  bookingIncomeModel: BookingIncome
  propertyPerformanceModel: PropertyPerformance
  faqModel: FrequentQuestionsModel
  bookingsByMonth: E.Either<PageStatus, BookingsByMonth>
  modal: O.Option<Modal>
  lastFocused: O.Option<string>
}

export type FinantialPosition = {
  outstanding: DealRentals["outstandingFis"]
}

export type BookingIncome = {
  breakdown: DealRentals["monthlyBreakdown"]
  upcommingBooking: DealRentals["dateOfNextBooking"]
}

export type PropertyPerformance = {
  bookingIncome: DealRentals["netBookingIncome"]
  occupancy: DealRentals["occupancy"]
  averageDailyRate: DealRentals["adr"]
  yieldToDate: DealRentals["yieldToDate"]
}

export type FrequentQuestionsModel = {
  faqs: { question: string; answer: string; shortAnswer: string }[]
  index: number
}

export const _init = "init"
export const _faqModal = "faqModal"
export const _modalContents = "modalContents"
export const _bookingsByMonthReq = "bookingsByMonthReq"
export const _bookingsByMonthRes = "bookingsByMonthRes"
export const _noOp = "noOp"
export const _lastFocused = "lastFocused"

export type Msg =
  | Variant.Tagged<typeof _init, void>
  | Variant.Tagged<typeof _faqModal, FrequentQuestionsModel>
  | Variant.Tagged<typeof _modalContents, O.Option<Modal>>
  | Variant.Tagged<typeof _bookingsByMonthReq, void>
  | Variant.Tagged<
      typeof _bookingsByMonthRes,
      E.Either<HttpError, BookingsByMonth>
    >
  | Variant.Tagged<typeof _noOp, void>
  | Variant.Tagged<typeof _lastFocused, O.Option<string>>

export const Msg = Variant.module<Msg>({
  init: value => Variant.tagged(_init, value),
  faqModal: value => Variant.tagged(_faqModal, value),
  modalContents: value => Variant.tagged(_modalContents, value),
  bookingsByMonthReq: value => Variant.tagged(_bookingsByMonthReq, value),
  bookingsByMonthRes: value => Variant.tagged(_bookingsByMonthRes, value),
  noOp: value => Variant.tagged(_noOp, value),
  lastFocused: value => Variant.tagged(_lastFocused, value),
})

export const initFrequentQuestionsModel: FrequentQuestionsModel = {
  faqs: [
    {
      question: "Statements",
      shortAnswer:
        "Monthly, you'll get a summary of bookings, balance, and earnings for the previous calendar month.",
      answer:
        "You will receive a statement each month with a summary of your bookings, outstanding balance and earnings for the last full calendar month. ",
    },
    {
      question: "Cleaning Fees",
      shortAnswer:
        "Guests' cleaning fees cover service, linen rentals, and materials, deducted from the booking amount.",
      answer:
        "Cleaning fees are paid for as part of the booking by the guests, this amount is deducted on our end to pay for the cleaning service, linen rentals and cleaning materials.",
    },
    {
      question: "Management Fees",
      shortAnswer:
        "Our fees cover 24/7 guest, property, and viewing management, plus pre-viewing tidy cleans.",
      answer:
        "Our management fees cover 24 hour guest management, property management, viewing management and tidy cleans before viewings to ensure the property is in prime condition. ",
    },
    {
      question: "Income after costs",
      shortAnswer: "Booking income after deducting management fees.",
      answer:
        "This is the booking income collected from guests, after the deduction of management fees.",
    },
    {
      question: "Payment to Balance",
      shortAnswer:
        "A percentage of net income is applied to the outstanding Flyp Investment Sum (FIS).",
      answer:
        "According to the fee split, we would allocate the % agreed of the income after costs to be applied to the outstanding FIS (Flyp Investment Sum).",
    },
    {
      question: "Payment to You",
      shortAnswer:
        "A percentage of net income is paid to you after settling the outstanding FIS.",
      answer:
        "According to the fee split, we would allocate the % agreed of the income after costs to be paid to you directly, normally after the outstanding FIS has been settled.",
    },
    {
      question: "Booking Platforms",
      shortAnswer:
        "We list properties on 70+ short-let platforms and with agents for longer bookings.",
      answer:
        "We list properties on about 70+ short let platforms, as well as listings with agents to acquire longer term bookings",
    },
    {
      question: "Guest Vetting",
      shortAnswer:
        "We rigorously vet guests, including ID checks, facial recognition, background checks, and more.",
      answer:
        "We ensure the safety and security of your property by rigorously vetting all guests. Our detailed process includes identity verification, facial recognition, background checks, and reference reviews. Additionally, we enforce age limits and flag any suspicious activities, ensuring that only trustworthy and reliable individuals book and stay in your home.",
    },
    {
      question: "Management Costs",
      shortAnswer:
        "Additional costs beyond the initial FIS require your approval unless in an emergency.",
      answer:
        "Any costs beyond the initial agreed Contract Investment Sum or Flyp Payment, this would be approved by you or in an emergency be spent on additional jobs such as leaks, heating issues etc.",
    },
  ],
  index: 0,
}

export const initRentalsModel = (data: DealRentals): RentalsModel => ({
  modal: O.none,
  lastFocused: O.none,
  bookingsByMonth: E.left("Loading"),
  finantialPositionModel: {
    outstanding: pipe(
      data.outstandingFis,
      O.map(({ gross, percentage }) => ({
        gross: Number(gross.toFixed(1)),
        percentage: Number(percentage.toFixed(1)),
      }))
    ),
  },
  bookingIncomeModel: {
    breakdown: data.monthlyBreakdown.map(booking => ({
      ...booking,
      totalIncome: Number(booking.totalIncome.toFixed(1)),
    })),
    upcommingBooking: data.dateOfNextBooking,
  },
  propertyPerformanceModel: {
    bookingIncome: pipe(
      data.netBookingIncome,
      O.map(netBookingIncome => Number(netBookingIncome.toFixed(1)))
    ),
    occupancy: pipe(data.occupancy, O.map(Math.round)),
    averageDailyRate: pipe(
      data.adr,
      O.map(adr => Math.round(adr))
    ),
    yieldToDate: pipe(
      data.yieldToDate,
      O.map(yieldToDate => yieldToDate)
    ),
  },
  faqModel: initFrequentQuestionsModel,
})

export const initBookingsModal = (
  data: RentalsMonthlyBreakdown
): RentalsMonthlyBreakdown => ({
  ...data,
  occupancy: Math.round(data.occupancy),
  totalIncome: Number(data.totalIncome.toFixed(1)),
  adr: Math.round(data.adr),
  bookings: pipe(
    data.bookings,
    A.sortBy([
      pipe(
        N.Ord,
        contramap((month: { checkIn: Date }) => month.checkIn.getTime() / 1000)
      ),
    ])
  ),
})

export type BookingsModal = {
  slide: number
  breakdown: DealRentals["monthlyBreakdown"]
}

export const _MonthlyBreakdown = "MonthlyBreakdown"
export const _FAQ = "FAQ"

export type Modal =
  | Variant.Tagged<typeof _MonthlyBreakdown, BookingsModal>
  | Variant.Tagged<typeof _FAQ, FrequentQuestionsModel>

export const Modal = Variant.module<Modal>({
  MonthlyBreakdown: value => Variant.tagged(_MonthlyBreakdown, value),
  FAQ: value => Variant.tagged(_FAQ, value),
})
